import { useQueries, useQuery } from "@tanstack/react-query";
import type { AxiosInstance } from "axios";
import { useApi } from "@/core/api";
import { reportKey } from "@/profitability/query";
import { reportPath } from "@/profitability/utils";
import type { CitationsAnswer } from "./types";

export interface CitationQuery {
  dataset: string;
  recordId: string;
  /** The column's key; null cites the record as a whole. */
  column: string | null;
}

export const citationsKey = (
  businessId: string,
  runId: string,
  { dataset, recordId, column }: CitationQuery,
) =>
  [
    ...reportKey(businessId),
    "citations",
    runId,
    dataset,
    recordId,
    column,
  ] as const;

const fetchCitations = (
  api: AxiosInstance,
  businessId: string,
  runId: string,
  { dataset, recordId, column }: CitationQuery,
  signal?: AbortSignal,
) =>
  api
    .get<CitationsAnswer>(`${reportPath(businessId, runId)}/citations`, {
      // The record id is JSON (`["48217"]`); axios encodes it.
      params: { dataset, record_id: recordId, ...(column ? { column } : {}) },
      signal,
    })
    .then((res) => res.data);

// A completed run's citations do not change: they are read from its output
// and the files it read, both fixed once it completes.
const STALE = 5 * 60_000;

export const useCitations = (
  businessId: string,
  runId: string,
  dataset: string,
  recordId: string,
  column: string | null,
) => {
  const api = useApi();
  const query = { dataset, recordId, column };
  return useQuery({
    queryKey: citationsKey(businessId, runId, query),
    queryFn: ({ signal }) =>
      fetchCitations(api, businessId, runId, query, signal),
    enabled: !!runId,
    staleTime: STALE,
  });
};

export const useFieldCitations = (
  businessId: string,
  runId: string,
  queries: CitationQuery[],
) => {
  const api = useApi();
  return useQueries({
    queries: queries.map((query) => ({
      queryKey: citationsKey(businessId, runId, query),
      queryFn: ({ signal }: { signal: AbortSignal }) =>
        fetchCitations(api, businessId, runId, query, signal),
      enabled: !!runId,
      staleTime: STALE,
    })),
  });
};
